import { useState, useEffect, useCallback, useMemo } from 'react';
import { useUrlState } from '../hooks/useUrlParams';
import { blogAPI, BASE_URL, API_URL } from '../services/api';
import JoditEditor from 'jodit-react';
import FilterBar, { SearchField, FilterSelect, CountPill } from '../components/FilterBar';
import Icon from '../components/Icon';

const s = {
  container: { width: '100%' },
  header: { marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  title: { fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: 0 },
  subtitle: { fontSize: '13px', color: '#64748b', marginTop: '2px' },
  card: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' },
  btn: { padding: '10px 20px', backgroundColor: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  td: { padding: '14px 16px', borderBottom: '1px solid #f1f5f9', fontSize: '14px', color: '#1e293b' },
  thumb: { width: '48px', height: '48px', borderRadius: '6px', objectFit: 'cover', backgroundColor: '#f1f5f9' },
  badge: { padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', cursor: 'pointer' },
  published: { backgroundColor: '#dcfce7', color: '#166534' },
  draft: { backgroundColor: '#fef3c7', color: '#92400e' },
  catBadge: { fontSize: '12px', color: '#7c3aed', backgroundColor: '#f5f3ff', padding: '4px 10px', borderRadius: '12px', fontWeight: '500' },
  iconBtn: { padding: '8px', border: 'none', borderRadius: '6px', backgroundColor: 'transparent', cursor: 'pointer', color: '#64748b' },
  empty: { textAlign: 'center', padding: '60px 20px', color: '#64748b' },
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalBox: { backgroundColor: '#fff', borderRadius: '16px', width: '90%', maxWidth: '420px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  modalHead: { padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  modalBody: { padding: '24px', overflowY: 'auto', flex: 1 },
  modalFoot: { padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#475569', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' },
  error: { padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', marginBottom: '16px', fontSize: '14px' },
  pag: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderTop: '1px solid #e2e8f0' },
  pagBtn: { padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#fff', cursor: 'pointer', fontSize: '13px', color: '#475569' },
  pagActive: { backgroundColor: '#4f46e5', color: '#fff', borderColor: '#4f46e5' },
  pagDisabled: { opacity: 0.5, cursor: 'not-allowed' },
};

const generateSlug = (t) => t.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');

const defaultForm = { title: '', slug: '', excerpt: '', content: '', image: '', category: '', tags: '', author: '', isPublished: false };

const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useUrlState('page', 1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Form state
  const [view, setView] = useState('list'); // 'list' | 'step1' | 'step2'
  const [editingBlog, setEditingBlog] = useState(null);
  const [formData, setFormData] = useState({ ...defaultForm });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  // Category modal
  const [showCatModal, setShowCatModal] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [catError, setCatError] = useState('');

  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (categoryFilter) params.category = categoryFilter;
      if (statusFilter) params.status = statusFilter;
      const res = await blogAPI.getAll(params);
      if (res.data.data) { setBlogs(res.data.data); setTotalPages(res.data.pagination?.totalPages || 1); setTotal(res.data.pagination?.total || 0); }
    } catch { setError('Failed to fetch blogs'); }
    finally { setLoading(false); }
  }, [page, search, categoryFilter, statusFilter]);

  const fetchCategories = useCallback(async () => {
    try { const res = await blogAPI.getCategories(); setCategories(res.data || []); } catch {}
  }, []);

  useEffect(() => { fetchBlogs(); }, [fetchBlogs]);
  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const openCreate = () => { setEditingBlog(null); setFormData({ ...defaultForm }); setFormError(''); setView('step1'); };

  const openEdit = async (blog) => {
    try {
      const res = await blogAPI.getById(blog.id);
      const d = res.data;
      setEditingBlog(d);
      setFormData({ title: d.title || '', slug: d.slug || '', excerpt: d.excerpt || '', content: d.content || '', image: d.image || '', category: d.category || '', tags: Array.isArray(d.tags) ? d.tags.join(', ') : (d.tags || ''), author: d.author || '', isPublished: d.isPublished || false });
      setFormError('');
      setView('step1');
    } catch { setError('Failed to load blog'); }
  };

  const handleTitleChange = (val) => {
    const u = { ...formData, title: val };
    if (!editingBlog) u.slug = generateSlug(val);
    setFormData(u);
  };

  const goToStep2 = () => {
    if (!formData.title) { setFormError('Title is required'); return; }
    setFormError('');
    setView('step2');
  };

  const handleSave = async () => {
    if (!formData.title) { setFormError('Title is required'); return; }
    try {
      setSaving(true); setFormError('');
      const payload = { ...formData, slug: formData.slug || generateSlug(formData.title), tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [] };
      if (editingBlog) await blogAPI.update(editingBlog.id, payload);
      else await blogAPI.create(payload);
      setView('list'); fetchBlogs(); fetchCategories();
    } catch (err) { setFormError(err.response?.data?.error || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try { await blogAPI.delete(id); setDeleteConfirm(null); fetchBlogs(); fetchCategories(); }
    catch { setError('Failed to delete'); }
  };

  const handleToggle = async (id) => {
    try { await blogAPI.togglePublish(id); fetchBlogs(); }
    catch { setError('Failed to toggle'); }
  };

  const uploadImage = async (file) => {
    // Client-side size guard matching server's 5MB limit
    if (file.size > 5 * 1024 * 1024) {
      setError(`File too large: ${(file.size / 1024 / 1024).toFixed(1)} MB. Maximum is 5 MB.`);
      return null;
    }
    const fd = new FormData(); fd.append('image', file);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/upload/blog-image`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd });
      // Surface non-JSON responses (e.g., nginx 413 HTML page) cleanly
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); }
      catch { setError(`Upload failed (HTTP ${res.status}). ${res.status === 413 ? 'Server rejected file as too large — check nginx client_max_body_size.' : text.slice(0, 140)}`); return null; }
      if (res.ok && data.success) return `/uploads/blogs/${data.filename}`;
      setError(data.error || `Upload failed (HTTP ${res.status})`);
    } catch (e) {
      setError(`Upload error: ${e.message}`);
    }
    return null;
  };

  const editorConfig = useMemo(() => ({
    readonly: false,
    height: 'calc(100vh - 180px)',
    minHeight: 500,
    placeholder: 'Write your blog content here...\n\nUse toolbar to add headings, images, tables, lists etc.',
    toolbarAdaptive: false,
    toolbarSticky: true,
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,
    uploader: {
      insertImageAsBase64URI: false,
      url: `${API_URL}/upload/blog-image`,
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      filesVariableName: () => 'image',
      isSuccess: (r) => r.success,
      getMsg: (r) => r.error || '',
      process: (r) => ({ files: [`/uploads/blogs/${r.filename}`], path: '', baseurl: BASE_URL, error: r.error ? 1 : 0, msg: r.error || '' }),
      defaultHandlerSuccess: function (data) { if (data.files?.length) this.s.insertImage(data.files[0]); }
    },
    buttons: [
      'bold', 'italic', 'underline', 'strikethrough', '|',
      'ul', 'ol', '|',
      'font', 'fontsize', 'brush', 'paragraph', '|',
      'image', 'video', 'table', 'link', 'hr', '|',
      'align', 'indent', 'outdent', '|',
      'undo', 'redo', '|',
      'eraser', 'fullsize', 'source'
    ]
  }), []);

  // ─── STEP 2: Full-screen Content Editor ───
  if (view === 'step2') {
    return (
      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 60px)' }}>
        {/* Top bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', backgroundColor: '#fff', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button onClick={() => setView('step1')} style={{ ...s.iconBtn, display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#4361ee', fontWeight: '500' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 18-6-6 6-6"/></svg>
              Back to Details
            </button>
            <span style={{ color: '#e2e8f0' }}>|</span>
            <span style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b' }}>{formData.title || 'Untitled'}</span>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button style={{ ...s.btn, backgroundColor: '#f1f5f9', color: '#475569' }} onClick={() => setView('list')}>Cancel</button>
            <button style={{ ...s.btn, opacity: saving ? 0.7 : 1 }} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : (editingBlog ? 'Update Blog' : 'Publish Blog')}
            </button>
          </div>
        </div>

        {formError && <div style={{ ...s.error, margin: '12px 20px 0' }}>{formError}</div>}

        {/* Editor */}
        <div style={{ flex: 1, overflow: 'hidden', padding: '0' }}>
          <JoditEditor
            value={formData.content || ''}
            config={editorConfig}
            onBlur={(c) => setFormData(prev => ({ ...prev, content: c }))}
          />
        </div>
      </div>
    );
  }

  // ─── STEP 1: Blog Details Form ───
  if (view === 'step1') {
    return (
      <div style={s.container}>
        {/* Header */}
        <div style={{ ...s.header, marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button onClick={() => setView('list')} style={{ ...s.iconBtn, display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', color: '#64748b' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 18-6-6 6-6"/></svg>
            </button>
            <div>
              <h1 style={s.title}>{editingBlog ? 'Edit Blog' : 'Create New Blog'}</h1>
              <p style={s.subtitle}>Step 1 of 2 — Fill in blog details, then write content</p>
            </div>
          </div>
        </div>

        {formError && <div style={s.error}>{formError}</div>}

        <div style={{ display: 'flex', gap: '24px' }}>
          {/* Left: Main fields */}
          <div style={{ flex: 1 }}>
            <div style={s.card}>
              <div style={{ padding: '24px' }}>
                {/* Title */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={s.label}>Blog Title *</label>
                  <input style={{ ...s.input, fontSize: '16px', padding: '12px 16px' }} placeholder="Enter an engaging blog title" value={formData.title} onChange={(e) => handleTitleChange(e.target.value)} />
                </div>

                {/* Slug */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={s.label}>URL Slug</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                    <span style={{ padding: '10px 12px', backgroundColor: '#f8fafc', color: '#94a3b8', fontSize: '13px', borderRight: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>/blogs/</span>
                    <input style={{ ...s.input, border: 'none', borderRadius: '0' }} placeholder="auto-generated-from-title" value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} />
                  </div>
                </div>

                {/* Excerpt */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={s.label}>Excerpt / Summary</label>
                  <textarea style={{ ...s.textarea, minHeight: '80px' }} placeholder="Brief description that appears in blog cards and SEO" value={formData.excerpt} onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })} rows={3} />
                </div>

                {/* Featured Image */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={s.label}>Featured Image</label>
                  {formData.image ? (
                    <div style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                      <img src={formData.image.startsWith('http') ? formData.image : `${BASE_URL}${formData.image}`} alt="Featured" style={{ width: '100%', height: '200px', objectFit: 'cover' }} />
                      <button onClick={() => setFormData({ ...formData, image: '' })} style={{ position: 'absolute', top: '8px', right: '8px', width: '28px', height: '28px', borderRadius: '50%', backgroundColor: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>&times;</button>
                    </div>
                  ) : (
                    <div style={{ border: '2px dashed #e2e8f0', borderRadius: '12px', padding: '30px', textAlign: 'center', cursor: 'pointer', backgroundColor: '#fafbfc' }}>
                      <input type="file" accept="image/*" id="blog-image-upload" style={{ display: 'none' }}
                        onChange={async (e) => {
                          const file = e.target.files[0]; if (!file) return;
                          const url = await uploadImage(file);
                          if (url) setFormData(prev => ({ ...prev, image: url }));
                        }}
                      />
                      <label htmlFor="blog-image-upload" style={{ cursor: 'pointer' }}>
                        <div style={{ fontSize: '32px', marginBottom: '8px', color: '#cbd5e1' }}>&#128247;</div>
                        <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>Click to upload featured image</p>
                        <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>JPG, PNG up to 5MB</p>
                      </label>
                    </div>
                  )}
                  <input style={{ ...s.input, marginTop: '8px' }} placeholder="Or paste image URL directly" value={formData.image} onChange={(e) => setFormData({ ...formData, image: e.target.value })} />
                </div>
              </div>
            </div>
          </div>

          {/* Right: Settings sidebar */}
          <div style={{ width: '300px', flexShrink: 0 }}>
            {/* Publish settings */}
            <div style={{ ...s.card, marginBottom: '16px' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
                <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b', margin: 0 }}>Publish Settings</h3>
              </div>
              <div style={{ padding: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: '16px' }}>
                  <div style={{ position: 'relative', width: '44px', height: '24px' }}>
                    <input type="checkbox" checked={formData.isPublished} onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })} style={{ position: 'absolute', opacity: 0, width: '100%', height: '100%', cursor: 'pointer', zIndex: 1 }} />
                    <div style={{ width: '44px', height: '24px', borderRadius: '12px', backgroundColor: formData.isPublished ? '#4f46e5' : '#e2e8f0', transition: 'background 0.2s', position: 'relative' }}>
                      <div style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#fff', position: 'absolute', top: '2px', left: formData.isPublished ? '22px' : '2px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                    </div>
                  </div>
                  <span style={{ fontSize: '14px', color: '#475569', fontWeight: '500' }}>{formData.isPublished ? 'Published' : 'Draft'}</span>
                </label>

                <button style={{ ...s.btn, width: '100%', justifyContent: 'center', fontSize: '15px', padding: '12px' }} onClick={goToStep2}>
                  Next: Write Content &rarr;
                </button>
                <button style={{ ...s.btn, width: '100%', justifyContent: 'center', backgroundColor: '#10b981', marginTop: '8px', opacity: saving ? 0.7 : 1 }} onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : (editingBlog ? 'Save Changes' : 'Save as Draft')}
                </button>
              </div>
            </div>

            {/* Category */}
            <div style={{ ...s.card, marginBottom: '16px' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b', margin: 0 }}>Category</h3>
                <button style={{ ...s.iconBtn, fontSize: '12px', color: '#4f46e5', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }} onClick={() => setShowCatModal(true)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
                  Manage
                </button>
              </div>
              <div style={{ padding: '16px 20px' }}>
                <select style={s.input} value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })}>
                  <option value="">Select Category</option>
                  {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </div>
            </div>

            {/* Author & Tags */}
            <div style={s.card}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
                <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b', margin: 0 }}>Additional Info</h3>
              </div>
              <div style={{ padding: '16px 20px' }}>
                <div style={{ marginBottom: '14px' }}>
                  <label style={s.label}>Author</label>
                  <input style={s.input} placeholder="Author name" value={formData.author} onChange={(e) => setFormData({ ...formData, author: e.target.value })} />
                </div>
                <div>
                  <label style={s.label}>Tags (comma separated)</label>
                  <input style={s.input} placeholder="e.g. challan, rto, insurance" value={formData.tags} onChange={(e) => setFormData({ ...formData, tags: e.target.value })} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── LIST VIEW ───
  if (loading && blogs.length === 0) return <div style={s.container}><p style={{ textAlign: 'center', color: '#64748b', padding: '40px' }}>Loading...</p></div>;

  return (
    <div style={s.container}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Blogs</h1>
          <p style={s.subtitle}>Manage blog posts, articles, and content</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button style={{ ...s.btn, backgroundColor: '#f1f5f9', color: '#475569' }} onClick={() => setShowCatModal(true)}>Categories</button>
          <button style={s.btn} onClick={openCreate}>+ Add Blog</button>
        </div>
      </div>

      {error && <div style={s.error}>{error}</div>}

      <div style={s.card}>
        <FilterBar right={<CountPill count={total} noun="blog" />}>
          <SearchField
            value={search}
            onChange={(v) => { setSearch(v); setPage(1); }}
            placeholder="Search blogs..."
          />
          <FilterSelect value={categoryFilter} onChange={(v) => { setCategoryFilter(v); setPage(1); }} aria-label="Category">
            <option value="">All Categories</option>
            {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
          </FilterSelect>
          <FilterSelect value={statusFilter} onChange={(v) => { setStatusFilter(v); setPage(1); }} aria-label="Status">
            <option value="">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </FilterSelect>
        </FilterBar>

        {blogs.length === 0 ? (
          <div style={s.empty}>
            <div style={{ marginBottom: '16px', color: '#cbd5e1', display: 'flex', justifyContent: 'center' }}><Icon name="file" size={44} /></div>
            <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>No blogs found</div>
            <p>Click "+ Add Blog" to create your first blog post.</p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>ID</th>
                    <th style={s.th}>Image</th>
                    <th style={s.th}>Title</th>
                    <th style={s.th}>Category</th>
                    <th style={s.th}>Status</th>
                    <th style={s.th}>Date</th>
                    <th style={s.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {blogs.map((b) => (
                    <tr key={b.id}>
                      <td style={s.td}>{b.id}</td>
                      <td style={s.td}>
                        {b.image ? <img src={b.image.startsWith('http') ? b.image : `${BASE_URL}${b.image}`} alt="" style={s.thumb} /> : <div style={{ ...s.thumb, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '18px' }}>&#128247;</div>}
                      </td>
                      <td style={{ ...s.td, fontWeight: '500', maxWidth: '250px' }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.title}</div>
                        {b.slug && <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>{b.slug}</div>}
                      </td>
                      <td style={s.td}>{b.category ? <span style={s.catBadge}>{b.category}</span> : '-'}</td>
                      <td style={s.td}>
                        <span style={{ ...s.badge, ...(b.isPublished ? s.published : s.draft) }} onClick={() => handleToggle(b.id)}>{b.isPublished ? 'Published' : 'Draft'}</span>
                      </td>
                      <td style={s.td}>{b.publishedAt ? new Date(b.publishedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</td>
                      <td style={s.td}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button style={s.iconBtn} onClick={() => openEdit(b)} title="Edit">
                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                          </button>
                          <button style={{ ...s.iconBtn, color: '#ef4444' }} onClick={() => setDeleteConfirm(b)} title="Delete">
                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div style={s.pag}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>Showing {blogs.length} of {total}</span>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button style={{ ...s.pagBtn, ...(page <= 1 ? s.pagDisabled : {}) }} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>Prev</button>
                  <span style={{ ...s.pagBtn, ...s.pagActive }}>{page}</span>
                  <button style={{ ...s.pagBtn, ...(page >= totalPages ? s.pagDisabled : {}) }} onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div style={s.modal} onClick={() => setDeleteConfirm(null)}>
          <div style={{ ...s.modalBox }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHead}>
              <span style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>Delete Blog</span>
              <button style={{ ...s.iconBtn, fontSize: '20px' }} onClick={() => setDeleteConfirm(null)}>&times;</button>
            </div>
            <div style={s.modalBody}>
              <p style={{ fontSize: '14px', color: '#475569', margin: 0 }}>Delete <strong>"{deleteConfirm.title}"</strong>? This cannot be undone.</p>
            </div>
            <div style={s.modalFoot}>
              <button style={{ padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', border: 'none', backgroundColor: '#f1f5f9', color: '#475569' }} onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button style={{ padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', border: 'none', backgroundColor: '#ef4444', color: '#fff' }} onClick={() => handleDelete(deleteConfirm.id)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCatModal && (
        <div style={s.modal} onClick={() => setShowCatModal(false)}>
          <div style={{ ...s.modalBox, maxWidth: '450px' }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHead}>
              <span style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>Manage Categories</span>
              <button style={{ ...s.iconBtn, fontSize: '20px' }} onClick={() => setShowCatModal(false)}>&times;</button>
            </div>
            <div style={s.modalBody}>
              {catError && <div style={s.error}>{catError}</div>}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                <input style={{ ...s.input, flex: 1 }} placeholder="New category name" value={newCatName} onChange={(e) => setNewCatName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (!newCatName.trim()) return; blogAPI.createCategory({ name: newCatName.trim() }).then(() => { setNewCatName(''); setCatError(''); fetchCategories(); }).catch(err => setCatError(err.response?.data?.error || 'Failed')); }}} />
                <button style={{ ...s.btn, whiteSpace: 'nowrap' }} onClick={() => { if (!newCatName.trim()) return; blogAPI.createCategory({ name: newCatName.trim() }).then(() => { setNewCatName(''); setCatError(''); fetchCategories(); }).catch(err => setCatError(err.response?.data?.error || 'Failed')); }}>+ Add</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {categories.length === 0 ? <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: '14px', padding: '20px 0' }}>No categories yet</p> : categories.map(c => (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#1e293b' }}>{c.name}</span>
                    <button style={{ ...s.iconBtn, color: '#ef4444', padding: '4px' }} onClick={() => { if (window.confirm(`Delete "${c.name}"?`)) blogAPI.deleteCategory(c.id).then(() => fetchCategories()).catch(err => setCatError(err.response?.data?.error || 'Failed')); }} title="Delete">
                      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Blogs;
